import { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { LoginContext } from "../LoginContext";
import { apiService } from "../backend/api";
import { getBaseUrl } from "../config/apiConfig";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck,
  Key,
  QrCode,
  Loader2,
  AlertCircle,
  Timer,
  ArrowLeft,
  Copy,
  CheckCircle2,
  KeyRound,
  X
} from "lucide-react";

const INACTIVITY_LIMIT = 30 * 60 * 1000; // 30 minutes
const STORAGE_KEY = "last2FAVerified";

/**
 * TotpGuard: Wraps a protected page with TOTP verification.
 * - On mount: checks if last2FAVerified timestamp is fresh (<30min) — if not, shows TOTP prompt
 * - If user has no TOTP set up yet, shows QR code for setup
 * - After first verification: shows backup codes that user must save
 * - After verification: shows children + inactivity timer
 * - After 30min inactivity: masks content and re-prompts TOTP
 */
const TotpGuard = ({ children }) => {
  const { t } = useTranslation();
  const { loginInfo } = useContext(LoginContext);
  const navigate = useNavigate();
  const username = loginInfo?.username;

  // TOTP states
  const [isVerified, setIsVerified] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [has2FA, setHas2FA] = useState(false);
  const [qrCodeImageUrl, setQrCodeImageUrl] = useState(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState("");

  // Backup code states
  const [backupCodes, setBackupCodes] = useState(null); // shown after first setup
  const [useBackupMode, setUseBackupMode] = useState(false);
  const [backupInput, setBackupInput] = useState("");
  const [copied, setCopied] = useState(false);

  // Inactivity timer
  const [secondsLeft, setSecondsLeft] = useState(INACTIVITY_LIMIT / 1000);
  const lastActivityTime = useRef(Date.now());
  const intervalRef = useRef(null);

  // Check if session is still valid on mount
  useEffect(() => {
    if (!username) return;
    const lastVerified = parseInt(localStorage.getItem(STORAGE_KEY) || "0", 10);
    const elapsed = Date.now() - lastVerified;
    if (lastVerified > 0 && elapsed < INACTIVITY_LIMIT) {
      setIsVerified(true);
      setIsLoading(false);
      lastActivityTime.current = Date.now();
      setSecondsLeft(Math.ceil((INACTIVITY_LIMIT - elapsed) / 1000));
    } else {
      localStorage.removeItem(STORAGE_KEY);
      fetch2FASetup(username);
    }
  }, [username]);

  // Inactivity timer (only runs when verified)
  useEffect(() => {
    if (!isVerified || !username) return;

    const resetActivity = () => {
      lastActivityTime.current = Date.now();
      setSecondsLeft(INACTIVITY_LIMIT / 1000);
    };

    const events = ["mousemove", "keydown", "mousedown", "touchstart", "scroll"];
    events.forEach(e => window.addEventListener(e, resetActivity));

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const diff = INACTIVITY_LIMIT - (now - lastActivityTime.current);
      if (diff <= 0) {
        localStorage.removeItem(STORAGE_KEY);
        setIsVerified(false);
        setCode("");
        setError("");
        setUseBackupMode(false);
        setBackupInput("");
        fetch2FASetup(username);
      } else {
        setSecondsLeft(Math.ceil(diff / 1000));
      }
    }, 1000);

    return () => {
      clearInterval(intervalRef.current);
      events.forEach(e => window.removeEventListener(e, resetActivity));
    };
  }, [isVerified, username]);

  const fetch2FASetup = useCallback(async (user) => {
    try {
      setIsLoading(true);
      setError("");
      const data = await apiService.request(`${getBaseUrl()}/api/user/2fa/setup`, {
        method: "POST",
        body: JSON.stringify({ username: user })
      });
      setHas2FA(data.has2FA);
      setQrCodeImageUrl(data.qrCodeImageUrl || null);
    } catch (err) {
      console.error("Error fetching 2FA setup:", err);
      setError(t("project2faVerify.setupError", "2FA-Einrichtung konnte nicht geladen werden."));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (code.length < 6) return;
    setError("");
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/2fa/verify`, {
        method: "POST",
        body: JSON.stringify({ username, token: code, forceNew: true })
      });
      if (data.verified) {
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        setCode("");
        // If backup codes returned → first-time setup, show them before granting access
        if (data.backupCodes) {
          setBackupCodes(data.backupCodes);
        } else {
          setIsVerified(true);
          lastActivityTime.current = Date.now();
          setSecondsLeft(INACTIVITY_LIMIT / 1000);
        }
      } else {
        setError(data.message || t("project2faVerify.invalidCode", "Ungültiger Code"));
      }
    } catch (err) {
      setError(err.message || t("project2faVerify.verificationFailed", "Verifizierung fehlgeschlagen"));
    }
  };

  const handleBackupSubmit = async (e) => {
    e.preventDefault();
    if (backupInput.length < 8) return;
    setError("");
    try {
      const data = await apiService.request(`${getBaseUrl()}/api/user/2fa/verify-backup`, {
        method: "POST",
        body: JSON.stringify({ username, backupCode: backupInput })
      });
      if (data.verified) {
        localStorage.setItem(STORAGE_KEY, Date.now().toString());
        setIsVerified(true);
        lastActivityTime.current = Date.now();
        setSecondsLeft(INACTIVITY_LIMIT / 1000);
        setBackupInput("");
        setUseBackupMode(false);
      }
    } catch (err) {
      setError(err.message || t("totpGuard.backupInvalid", "Ungültiger Backup-Code"));
    }
  };

  const handleCopyBackupCodes = () => {
    if (!backupCodes) return;
    navigator.clipboard.writeText(backupCodes.join("\n"));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleBackupCodesSaved = () => {
    setBackupCodes(null);
    setIsVerified(true);
    lastActivityTime.current = Date.now();
    setSecondsLeft(INACTIVITY_LIMIT / 1000);
  };

  // Not logged in — just render children (ProtectedRoute handles login)
  if (!username) return children;

  // Show backup codes after first setup
  if (backupCodes) {
    return (
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[9999] flex items-center justify-center p-4 font-sans">
        <div className="bg-white dark:bg-slate-800 w-full max-w-[480px] rounded-[2rem] shadow-2xl overflow-hidden border border-white/20 dark:border-slate-700 animate-in zoom-in duration-300">
          {/* Header */}
          <div className="bg-emerald-600 dark:bg-emerald-500/20 p-6 text-center border-b border-white/10 dark:border-slate-700">
            <div className="flex items-center justify-center space-x-4">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white backdrop-blur-sm border border-white/10 shadow-lg">
                <KeyRound size={24} strokeWidth={1.5} />
              </div>
              <h1 className="text-lg font-bold text-white leading-tight">
                {t("totpGuard.backupCodesTitle", "Backup-Codes sichern")}
              </h1>
            </div>
          </div>

          <div className="p-8 space-y-6">
            <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-xl p-4">
              <p className="text-[11px] text-amber-800 dark:text-amber-300 font-bold leading-relaxed">
                {t("totpGuard.backupCodesWarning", "Speichern Sie diese Codes sicher ab. Sie werden nur einmal angezeigt und können verwendet werden, falls Sie keinen Zugang zu Ihrer Authenticator-App haben.")}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 p-4 bg-slate-50 dark:bg-slate-700 rounded-xl border border-slate-200 dark:border-slate-600">
              {backupCodes.map((bc, i) => (
                <div key={i} className="text-center font-mono text-sm font-bold text-slate-800 dark:text-white py-1.5 bg-white dark:bg-slate-600 rounded-lg border border-slate-100 dark:border-slate-500">
                  {bc}
                </div>
              ))}
            </div>

            <button
              onClick={handleCopyBackupCodes}
              className="flex items-center justify-center space-x-2 w-full h-10 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-xs font-bold"
            >
              {copied ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Copy size={14} />}
              <span>{copied ? t("totpGuard.copied", "Kopiert!") : t("totpGuard.copyCodes", "Codes kopieren")}</span>
            </button>

            <button
              onClick={handleBackupCodesSaved}
              className="w-full h-12 rounded-xl font-bold bg-emerald-600 dark:bg-emerald-500 text-white dark:text-slate-900 shadow-lg transition-all flex items-center justify-center space-x-3 active:scale-[0.98]"
            >
              <CheckCircle2 size={18} />
              <span className="text-sm font-bold">{t("totpGuard.codesSaved", "Codes gesichert — Weiter")}</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Verified — show children with inactivity timer
  if (isVerified) {
    const minutes = Math.floor(secondsLeft / 60).toString().padStart(2, "0");
    const secs = (secondsLeft % 60).toString().padStart(2, "0");
    const isUrgent = secondsLeft <= 120;

    return (
      <>
        {/* Fixed TOTP timer bar below header */}
        <div className={`fixed top-20 left-0 right-0 z-40 flex items-center justify-center gap-2 py-1.5 px-4 text-xs font-bold transition-colors ${
          isUrgent
            ? 'bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 border-b border-red-100 dark:border-red-900/30'
            : 'bg-amber-50 dark:bg-amber-950/30 text-amber-700 dark:text-amber-400 border-b border-amber-100 dark:border-amber-900/30'
        }`}>
          <Timer size={14} />
          <span className="text-[10px] opacity-70 uppercase tracking-wider">{t("totpGuard.label", "TOTP Verifizierung")}</span>
          <span className="font-mono">{minutes}:{secs}</span>
        </div>
        {/* Spacer for fixed bar */}
        <div className="h-8" />
        {children}
      </>
    );
  }

  // Not verified — show TOTP prompt (fullscreen overlay).
  // Both the backdrop click and the X icon abort the setup the same way as
  // the Abbrechen button — navigate(-1) goes back to the previous page. Since
  // the server now keeps the secret as "unverified" until a code is confirmed
  // (see /2fa/setup), aborting here can't leave the user permanently locked.
  const handleAbort = () => navigate(-1);
  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-md z-[9999] flex items-center justify-center p-4 font-sans">
      <div
        className={`relative bg-white dark:bg-slate-800 w-full ${qrCodeImageUrl && !useBackupMode ? 'max-w-[640px]' : 'max-w-[420px]'} rounded-[2rem] shadow-2xl overflow-hidden border border-white/20 dark:border-slate-700 animate-in zoom-in duration-300`}
      >

        {/* Header */}
        <div className="bg-[#0f7699] dark:bg-[#4fc3f7]/20 p-6 text-center border-b border-white/10 dark:border-slate-700 relative">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center text-white backdrop-blur-sm border border-white/10 shadow-lg">
              <ShieldCheck size={24} strokeWidth={1.5} />
            </div>
            <h1 className="text-lg font-bold text-white leading-tight">
              {useBackupMode
                ? t("totpGuard.backupCodeTitle", "Backup-Code eingeben")
                : has2FA
                  ? t("project2faVerify.title", "Zwei-Faktor-Verifizierung")
                  : t("project2faVerify.setupTitle", "Sicherheit einrichten")}
            </h1>
          </div>
          {/* X close button — same effect as Abbrechen / backdrop click */}
          <button
            type="button"
            onClick={handleAbort}
            aria-label={t("totpGuard.cancel", "Abbrechen")}
            className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-lg text-white/80 hover:bg-white/15 hover:text-white transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-8">
          {isLoading ? (
            <div className="py-8 flex flex-col items-center justify-center space-y-4">
              <Loader2 size={40} className="text-[#0f7699] dark:text-[#4fc3f7] animate-spin" />
              <p className="text-slate-500 dark:text-slate-400 text-sm font-medium">{t("project2faVerify.connecting", "Verbindung wird aufgebaut...")}</p>
            </div>
          ) : useBackupMode ? (
            /* Backup code input mode */
            <div className="space-y-6">
              <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                {t("totpGuard.backupCodeDescription", "Geben Sie einen Ihrer einmaligen Backup-Codes ein, um sich zu verifizieren.")}
              </p>
              <form onSubmit={handleBackupSubmit} className="space-y-4">
                <div className="space-y-2">
                  <label className="text-[10px] font-black uppercase tracking-widest px-1 text-[#0f7699] dark:text-[#4fc3f7] opacity-70">
                    {t("totpGuard.backupCodeLabel", "Backup-Code")}
                  </label>
                  <div className="relative group">
                    <KeyRound className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-500 group-focus-within:text-[#0f7699] dark:group-focus-within:text-[#4fc3f7] transition-colors" size={18} />
                    <input
                      type="text"
                      value={backupInput}
                      onChange={(e) => setBackupInput(e.target.value.replace(/[^a-fA-F0-9]/g, "").slice(0, 8))}
                      maxLength={8}
                      placeholder="a1b2c3d4"
                      autoFocus
                      className="w-full h-12 pl-12 pr-6 bg-slate-50 dark:bg-slate-700 border-2 border-slate-100 dark:border-slate-600 rounded-xl focus:border-[#0f7699]/40 dark:focus:border-[#4fc3f7]/40 focus:bg-white dark:focus:bg-slate-600 transition-all text-xl font-bold tracking-[0.3em] text-slate-800 dark:text-white placeholder:text-slate-200 dark:placeholder:text-slate-500 outline-none caret-[#0f7699] dark:caret-[#4fc3f7] font-mono"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={backupInput.length < 8}
                  className={`w-full h-12 rounded-xl font-bold shadow-lg shadow-blue-900/5 transition-all flex items-center justify-center space-x-3 active:scale-[0.98] ${
                    backupInput.length < 8
                      ? 'bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-500'
                      : 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900'
                  }`}
                >
                  <ShieldCheck size={18} />
                  <span className="text-sm font-bold">{t("project2faVerify.confirm", "Bestätigen & Aktivieren")}</span>
                </button>
              </form>

              {error && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 text-red-600 dark:text-red-400 rounded-xl flex items-center space-x-3 animate-in fade-in slide-in-from-top-2">
                  <AlertCircle size={16} className="shrink-0" />
                  <p className="text-[11px] font-bold leading-tight">{error}</p>
                </div>
              )}

              <div className="pt-6 border-t border-slate-100 dark:border-slate-700 flex gap-3">
                <button
                  onClick={() => { setUseBackupMode(false); setError(""); setBackupInput(""); }}
                  className="flex-1 flex items-center justify-center space-x-2 h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-[10px] font-black uppercase tracking-widest"
                >
                  <Key size={12} />
                  <span>{t("totpGuard.useTotp", "TOTP-Code nutzen")}</span>
                </button>
                <button
                  onClick={() => navigate(-1)}
                  className="flex items-center justify-center space-x-2 h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-[10px] font-black uppercase tracking-widest"
                >
                  <ArrowLeft size={12} />
                  <span>{t("totpGuard.cancel", "Abbrechen")}</span>
                </button>
              </div>
            </div>
          ) : (
            <div className={`flex flex-col ${qrCodeImageUrl ? 'md:flex-row gap-10' : 'space-y-6'}`}>

              {/* QR Code (only for setup) */}
              {qrCodeImageUrl && (
                <div className="flex flex-col items-center space-y-4 md:w-[220px] flex-shrink-0">
                  <div className="p-4 bg-white rounded-[1.5rem] border-2 border-slate-200 dark:border-slate-500 shadow-inner">
                    <img src={qrCodeImageUrl} alt="2FA QR Code" className="w-36 h-36" />
                  </div>
                  <div className="bg-[#0f7699]/5 dark:bg-[#4fc3f7]/10 border-[#0f7699]/10 dark:border-[#4fc3f7]/20 p-3 rounded-xl border w-full">
                    <div className="flex items-center space-x-2 mb-1.5 text-[#0f7699] dark:text-[#4fc3f7]">
                      <QrCode size={16} />
                      <span className="text-[10px] font-black uppercase tracking-wider">Scan Me</span>
                    </div>
                    <p className="text-[10px] leading-relaxed text-slate-500 dark:text-slate-400 font-medium italic">
                      {t("project2faVerify.scanInstructions", "Scannen Sie diesen Code mit Ihrer Authentifizierungs-App.")}
                    </p>
                  </div>
                </div>
              )}

              {/* Form */}
              <div className="flex-1 flex flex-col justify-between space-y-6">
                <div className="space-y-6">
                  {!has2FA && (
                    <div className="space-y-2">
                      <h2 className="text-xs font-black uppercase tracking-widest text-[#0f7699] dark:text-[#4fc3f7]">
                        {t("project2faVerify.instructions", "Anleitung")}
                      </h2>
                      <p className="text-[11px] text-slate-600 dark:text-slate-400 leading-relaxed">
                        {t("project2faVerify.setupDescription", "Um 2FA zu aktivieren, scannen Sie den QR-Code oder geben Sie den generierten Code ein.")}
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest px-1 text-[#0f7699] dark:text-[#4fc3f7] opacity-70">
                        {t("project2faVerify.codeLabel", "Einmal-Code")}
                      </label>
                      <div className="relative group">
                        <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 dark:text-slate-500 group-focus-within:text-[#0f7699] dark:group-focus-within:text-[#4fc3f7] transition-colors" size={18} />
                        <input
                          type="text"
                          value={code}
                          onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                          maxLength={6}
                          placeholder="000 000"
                          autoFocus
                          className="w-full h-12 pl-12 pr-6 bg-slate-50 dark:bg-slate-700 border-2 border-slate-100 dark:border-slate-600 rounded-xl focus:border-[#0f7699]/40 dark:focus:border-[#4fc3f7]/40 focus:bg-white dark:focus:bg-slate-600 transition-all text-xl font-bold tracking-[0.5em] text-slate-800 dark:text-white placeholder:text-slate-200 dark:placeholder:text-slate-500 outline-none caret-[#0f7699] dark:caret-[#4fc3f7]"
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={code.length < 6}
                      className={`w-full h-12 rounded-xl font-bold shadow-lg shadow-blue-900/5 transition-all flex items-center justify-center space-x-3 active:scale-[0.98] ${
                        code.length < 6
                          ? 'bg-slate-100 dark:bg-slate-700 text-slate-300 dark:text-slate-500'
                          : 'bg-[#0f7699] dark:bg-[#4fc3f7] text-white dark:text-slate-900'
                      }`}
                    >
                      <ShieldCheck size={18} />
                      <span className="text-sm font-bold">{t("project2faVerify.confirm", "Bestätigen & Aktivieren")}</span>
                    </button>
                  </form>

                  {error && (
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-800/30 text-red-600 dark:text-red-400 rounded-xl flex items-center space-x-3 animate-in fade-in slide-in-from-top-2">
                      <AlertCircle size={16} className="shrink-0" />
                      <p className="text-[11px] font-bold leading-tight">{error}</p>
                    </div>
                  )}

                </div>

                {/* Footer */}
                <div className="pt-6 border-t border-slate-100 dark:border-slate-700 mt-auto flex gap-3">
                  {has2FA && (
                    <button
                      onClick={() => { setUseBackupMode(true); setError(""); setCode(""); }}
                      className="flex-1 flex items-center justify-center space-x-2 h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-[10px] font-black uppercase tracking-widest"
                    >
                      <KeyRound size={12} />
                      <span>{t("totpGuard.useBackup", "Backup-Code")}</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate(-1)}
                    className={`flex items-center justify-center space-x-2 h-10 px-4 rounded-lg border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-600 transition-all text-[10px] font-black uppercase tracking-widest ${has2FA ? '' : 'w-full'}`}
                  >
                    <ArrowLeft size={12} />
                    <span>{t("totpGuard.cancel", "Abbrechen")}</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TotpGuard;
